import {
    FAVORITE_FORM_UPDATED,
    FAVORITE_FORMS_LOADED,
    FORM_CLOSED,
    FORM_CUSTOM_TITLE_CHANGED,
    FORM_ITEM_INSTANCE_CLOSE_RESTRICTIONS_UPDATED,
    FORM_OPENED,
    FORM_OVERLAY_CLICKED,
    FORM_PROFILE_CHANGED,
    FORM_SWITCHED,
    HISTORY_FORMS_INFO_UPDATED,
    IFavoriteFormsLoadedAction,
    IFavoriteFormUpdatedAction,
    IFormClosedAction,
    IFormCustomTitleChangedAction,
    IFormItemInstanceCloseRestrictionsUpdatedAction,
    IFormOpenedAction,
    IFormOverlayClickedAction,
    IFormProfileChangedAction,
    IFormSwitchedAction,
    IHistoryFormsInfoUpdatedAction,
    ILocalizationItemsLoadedAction,
    ILocalStorageKeyLoadedAction,
    IMenuItemCustomActionProcessedAction,
    IMenuItemsLoadedAction,
    INotificationGroupsLoadedAction,
    INotificationItemsLoadedAction,
    ITaskResultDetailLoadedAction,
    IUserInfoLoadedAction,
    LOCAL_STORAGE_KEY_LOADED,
    LOCALIZATION_ITEMS_LOADED,
    MENU_ITEM_CUSTOM_ACTION_PROCESSED,
    MENU_ITEMS_LOADED,
    MenuItemCustomActionErrorPayload,
    NOTIFICATION_GROUPS_LOADED,
    NOTIFICATION_ITEMS_LOADED,
    TASK_RESULT_DETAIL_LOADED,
    USER_INFO_LOADED,
    UserInfo,
} from "@multiweb/reactCore/bridge/api";
import { IFormProfile, ILocalizationItem, IMenuData, INotificationGroup, INotificationItem, IOpenedFormHistoryInfo, ITaskResultDetail, NotificationGroupId } from "@multiweb/types/contracts";
import { IMainMenuItemActionResult, IOpenedFormInfo, ProfileChangeType } from "@multiweb/types/multiweb";
// Import createResultAction nemůže být v bridge modulu z @multiweb/types/react kvůli cyklickým závislostem.
import { createResultAction } from "../../../resultActions";

/**
 * Tento soubor poskytuje action creatory pro akce, jejichž typy jsou sice veřejné,
 * ale které by se měly vytvářet pouze v bridge modulu. Proto nejsou tyto action
 * creatory součástí bridge api.
 */

/**
 * Akce indikující načtení oblíbených formulářů.
 * @param favoriteFormCodes Kódy oblíbených formulářů.
 */
export const createFavoriteFormsLoadedAction = (favoriteForms: readonly { menuId: number; formCode: string; profileId?: number }[]): IFavoriteFormsLoadedAction => ({
    type: FAVORITE_FORMS_LOADED,
    payload: {
        favoriteForms,
    },
});

/**
 * Akce indikující změnu v historii otevřených formulářů.
 * @param historyFormsInfo Nová data historie. Nejnovější záznam v historii je na konci pole.
 */
export const createHistoryFormsInfoUpdatedAction = (historyFormsInfo: ReadonlyArray<IOpenedFormHistoryInfo>): IHistoryFormsInfoUpdatedAction => ({
    type: HISTORY_FORMS_INFO_UPDATED,
    payload: {
        historyFormsInfo,
    },
});

/**
 * Akce indikující, že načtení lokalizačních položek proběhlo v pořádku.
 * @param localizationItems Načtené lokalizační položky.
 * @param key Klíč pro jednoznačnou identifikaci požadavku. Umožňuje rozlišit více akcí tohoto typu z různých zdrojů.
 */
export const createLocalizationItemsLoadedAction = (localizationItems: ReadonlyMap<string, ILocalizationItem>, key: string): ILocalizationItemsLoadedAction => ({
    type: LOCALIZATION_ITEMS_LOADED,
    payload: {
        key,
        localizationItems,
    },
});

/**
 * Akce indikující, že načtení hodnoty z local storage proběhlo v pořádku.
 * @param key Klíč.
 * @param value Hodnota která byla načtena.
 */
export const createLocalStorageKeyLoadedAction = (key: string, value: string): ILocalStorageKeyLoadedAction => ({
    type: LOCAL_STORAGE_KEY_LOADED,
    payload: {
        key,
        value,
    },
});

/**
 * Akce indikující dokončené zpracování custom akce pro položku menu.
 * @param menuItemActionResult Výsledek zpracování, pokud je úspěšné.
 * @param error Chybový objekt, pokud se zpracování nezdařilo.
 */
export const createMenuItemCustomActionProcessedAction = (menuItemActionResult?: IMainMenuItemActionResult, error?: MenuItemCustomActionErrorPayload): IMenuItemCustomActionProcessedAction =>
    createResultAction(MENU_ITEM_CUSTOM_ACTION_PROCESSED, menuItemActionResult, error);

/**
 * Akce indikující, že načtení dat položek menu proběhlo v pořádku.
 *
 * @param menuItems Načtené položky pro menu.
 */
export const createMenuItemsLoadedAction = (menuItems: IMenuData): IMenuItemsLoadedAction => ({
    type: MENU_ITEMS_LOADED,
    payload: {
        menuItems,
    },
});

/**
 * Akce indikující, že načtení notifikačních skupin proběhlo v pořádku.
 * @param notificationGroups Načtené notifikační skupiny
 */
export const createNotificationGroupsLoadedAction = (notificationGroups: ReadonlyArray<INotificationGroup>): INotificationGroupsLoadedAction => ({
    type: NOTIFICATION_GROUPS_LOADED,
    payload: {
        notificationGroups,
    },
});

/**
 * Akce indikující, že načtení notifikačních položek proběhlo v pořádku.
 * @param notificationGroupId Identifkátor notifikační skupiny notifikačních položek
 * @param notificationItems Načtené notifikační položky
 */
export const createNotificationItemsLoadedAction = (notificationGroupId: NotificationGroupId, notificationItems: ReadonlyArray<INotificationItem>): INotificationItemsLoadedAction => ({
    type: NOTIFICATION_ITEMS_LOADED,
    payload: {
        notificationGroupId,
        notificationItems,
    },
});

/**
 * Akce indikující, že načtení detailu o výsledku úlohy proběhlo v pořádku.
 * @param taskResultDetail Detail o výsledku úlohy.
 */
export const createTaskResultDetailsLoadedAction = (taskResultDetail: ITaskResultDetail): ITaskResultDetailLoadedAction => ({
    type: TASK_RESULT_DETAIL_LOADED,
    payload: {
        taskResultDetail,
    },
});

/**
 * Akce indikující, že načtení dat o přihlášeném uživateli proběhlo v pořádku.
 *
 * @param userInfo Objekt obsahující informace o uživateli.
 */
export const createUserInfoLoadedAction = (userInfo: UserInfo): IUserInfoLoadedAction => ({
    type: USER_INFO_LOADED,
    payload: {
        userInfo,
    },
});

/**
 * Akce indikující změnu oblíbenosti formuláře.
 * @param menuId ID položky menu.
 * @param formCode Kód změněného formuláře.
 * @param isFavorite True pokud byl formulář přidán do oblíbených, false pokud byl odebrán.
 */
export const createFavoriteFormUpdatedAction = (menuId: number, formCode: string, isFavorite: boolean, profile?: IFormProfile): IFavoriteFormUpdatedAction => ({
    type: FAVORITE_FORM_UPDATED,
    payload: {
        menuId,
        formCode,
        isFavorite,
        profile,
    },
});

/**
 * Akce indikující změnu custom title ve formuláři.
 * @param formInstanceId Id otevřeného formuláře, u kterého se změnil custom title.
 * @param customTitle Nová hodnota custom title.
 */
export const createFormCustomTitleChangedAction = (formInstanceId: string, customTitle: string): IFormCustomTitleChangedAction => ({
    type: FORM_CUSTOM_TITLE_CHANGED,
    payload: {
        formInstanceId,
        customTitle,
    },
});

/**
 * Akce indikující zavření formuláře.
 *
 * @param closedFormInfo Informace o zavřeném formuláři.
 */
export const createFormClosedAction = (closedFormInfo: IOpenedFormInfo): IFormClosedAction => ({
    type: FORM_CLOSED,
    payload: {
        closedFormInfo,
    },
});

/**
 * Akce indikující aktualizaci omezení pro zobrazení akční položky zavření instance formulářové položky.
 * @param formInstanceId Id instance otevřené formulářové položky, pro kterou se nastaví omezení.
 * @param hideCloseAction True zapne omezení akce zavření, false vypne.
 */
export const createFormItemCloseRestrictionsUpdatedAction = (formInstanceId: string, hideCloseAction: boolean): IFormItemInstanceCloseRestrictionsUpdatedAction => ({
    type: FORM_ITEM_INSTANCE_CLOSE_RESTRICTIONS_UPDATED,
    payload: {
        formInstanceId,
        hideCloseAction,
    },
});

/**
 * Akce indikující, že proběhlo otevření formuláře.
 *
 * @param openedFormInfo Informace identifikující otevřený formulář.
 * @param hideWindowActions Indikátor, zda položka menu, která způsobila otevření tohoto formuláře, má mít skryté akce pro otevření.
 * @param openInBackground Indikátor, zda se formulář otevírá na pozadí a položka menu se tedy nemá zaktivňovat.
 */
export const createFormOpenedAction = (openedFormInfo: IOpenedFormInfo, hideWindowActions: boolean, openInBackground = false): IFormOpenedAction => ({
    type: FORM_OPENED,
    payload: {
        openedFormInfo,
        hideWindowActions,
        openInBackground,
    },
});

/**
 * Akce indikující kliknutí na overlay nad formulářovou částí.
 */
export const createFormOverlayClickedAction = (): IFormOverlayClickedAction => ({
    type: FORM_OVERLAY_CLICKED,
});

/**
 * Akce indikující změnu profilu formuláře
 * @param type Typ změny
 * @param profile Změněný profil
 */
export const createFormProfileChangedAction = (type: ProfileChangeType, profile: IFormProfile): IFormProfileChangedAction => ({
    type: FORM_PROFILE_CHANGED,
    payload: {
        type,
        profile,
    },
});

/**
 * Akce indikující dokončení přepnutí na formulář.
 * @param switchedFormInfo Informace o formuláři, na který se přepnulo.
 */
export const createFormSwitchedAction = (switchedFormInfo: IOpenedFormInfo): IFormSwitchedAction => ({
    type: FORM_SWITCHED,
    payload: {
        switchedFormInfo,
    },
});
